import { useEffect, type ReactNode } from 'react';
import { useWhiteLabel } from '@/hooks/useWhiteLabel';
import { WhiteLabelContext, type WhiteLabelSettings } from './whiteLabelContext';

interface WhiteLabelProviderProps {
  children: ReactNode;
}

export function WhiteLabelProvider({ children }: WhiteLabelProviderProps) {
  const { settings, hasAccess, loading, error } = useWhiteLabel();

  useEffect(() => {
    if (settings && hasAccess) {
      const root = document.documentElement;
      root.style.setProperty('--wl-primary-color', settings.primaryColor);
      root.style.setProperty('--wl-secondary-color', settings.secondaryColor);
      if (settings.faviconUrl) {
        updateFavicon(settings.faviconUrl);
      }
      updateBranding(settings);
    }
    return () => {
      const root = document.documentElement;
      root.style.removeProperty('--wl-primary-color');
      root.style.removeProperty('--wl-secondary-color');
      resetFavicon();
    };
  }, [settings, hasAccess]);

  return (
    <WhiteLabelContext.Provider value={{ settings, hasAccess, loading, error }}>
      {children}
    </WhiteLabelContext.Provider>
  );
}

function updateFavicon(faviconUrl: string) {
  const existingFavicon = document.querySelector('link[rel="icon"]');
  if (existingFavicon) {
    existingFavicon.setAttribute('href', faviconUrl);
  } else {
    const newFavicon = document.createElement('link');
    newFavicon.rel = 'icon';
    newFavicon.href = faviconUrl;
    document.head.appendChild(newFavicon);
  }
}

function resetFavicon() {
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon) {
    favicon.setAttribute('href', '/favicon.ico');
  }
}

function updateBranding(_settings: WhiteLabelSettings) {
  const titleTag = document.querySelector('title');
  if (titleTag) {
    // Could add custom meta tags here for white label organizations
  }
}
